<script lang="ts">
	import Filters from '$lib/components/category/Filters.svelte'
	import CategoryProducts from '$lib/components/category/CategoryProducts.svelte'
	import SortItems from '$lib/components/common/SortItems.svelte'
	import HeroSearch from '$lib/components/common/search/HeroSearch.svelte'
	import IntersectionObserver from 'svelte-intersection-observer'
	import { getMoreSearchProducts, loadMoreProducts } from '$lib/js/products'
	import { page } from '$app/stores'
	import { storeCurrentPage, storeMaxPrice, storeNextPageURL, storeSearchedText } from '$lib/js/stores'
	import { onMount } from 'svelte'
	import { afterNavigate } from '$app/navigation'
	import { t } from 'svelte-i18n-lingui'
	import type { BaseProduct, FullProduct, ProductQueryOptions } from '$lib/js/elasticsearch/types'
	import { getIntSearchParam, orderCategoryResults } from '$lib/js/helpers'
	import { registerSearchEvent } from '$lib/js/ga4/events'
	import { getBrandsParam, getCatsParam } from '$lib/js/helpers/handlers'
	import { getContextScope } from '$lib/js/helpers/i18n'

	$: $storeSearchedText = $page.url.searchParams.get('q') || ''

	let products: (BaseProduct | FullProduct)[] = $page.data.products

	const scope = getContextScope(),
		endpoint = 'search'

	afterNavigate(() => {
		products = $page.data.products
	})

	onMount(() => {
		$storeMaxPrice = parseInt(($page.data.max_price.value * 1.21 + 1).toFixed(0))

		registerSearchEvent($storeSearchedText)
	})

	/* Infinite scroll */
	let elementOnce: HTMLElement

	/* Order & filter products */
	let options: ProductQueryOptions & { search_text?: string } = {
		start: getIntSearchParam($page.url, 'p', 1),
		sort: orderCategoryResults($page.url.searchParams.get('sort') || 'default'),
		min_price: getIntSearchParam($page.url, 'minPrice', 0),
		max_price: getIntSearchParam($page.url, 'maxPrice', 200000),
		search_text: $storeSearchedText,
		brand_ids: getBrandsParam($page.url, 'brands', $page.data.brands),
		cat_ids: getCatsParam($page.url, 'cats', $page.data.subcats),
		cat_id: undefined,
	}

	/* Get results page number */
	let allResults = false
</script>

<HeroSearch />

{#key $storeSearchedText}
	<div class="container search">
		<div class="search__content">
			<div class="category__actions">
				<div class="category__actions__block">
					<Filters bind:products bind:options {endpoint} />
				</div>
				<div class="sort-items__mobile">
					<SortItems bind:products bind:options {endpoint} />
				</div>
			</div>

			<div class="category__products">
				<CategoryProducts bind:products bind:allResults />
			</div>
		</div>

		<IntersectionObserver
			element={elementOnce}
			rootMargin="800px"
			on:observe={async (e) => {
				if (e.detail.isIntersecting === true) {
					await getMoreSearchProducts(options, {scope: scope}).then((response) => {
						allResults = false

						if (response.length === 0) {
							allResults = true
						} else {
							products = loadMoreProducts(products, response)
						}
					})
				}
			}}
		>
			<div bind:this={elementOnce} />
		</IntersectionObserver>

		<a href={$storeNextPageURL} style="visibility: hidden">{$t`Page ${$storeCurrentPage + 1}`}</a>
	</div>
{/key}

<style lang="scss">
  .search {
    margin-bottom: 60px;

    @media (min-width: 769px) {
      margin-bottom: 120px;
    }

    &__content {
      min-height: 1200px;

      @media (min-width: 769px) {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
      }
    }
  }

  .sort-items {
    &__mobile {
      @media (min-width: 1025px) {
        display: none;
      }

      @media (max-width: 1024px) {
        margin: 12px 20px 12px 12px;
      }
    }
  }

  .category {
    &__actions {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;

      &__block {
        @media (max-width: 1024px) {
          flex-grow: 1;
        }
        @media (min-width: 1025px) {
          padding: 17px 21px;
          box-shadow: 0px 0px 20px 10px rgba(0, 0, 0, 0.12);
          border-radius: 8px;
        }
      }

      @media (max-width: 1024px) {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: #f0f0f0;
        z-index: 9;
        box-shadow: 0px 0px 20px 10px rgba(0, 0, 0, 0.12);
      }

      @media (min-width: 1025px) {
        position: sticky;
        top: 140px;
      }
    }

    &__products {
      @media (min-width: 1025px) {
        width: calc(100% - 330px);
      }
    }
  }
</style>
